// Angular 
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { CommonModule, PathLocationStrategy } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { Location, LocationStrategy } from '@angular/common';
import { ErrorHandler } from '@angular/core';
import { ChartsModule } from 'ng2-charts/ng2-charts';

import { TextareaAutosizeModule } from 'ngx-textarea-autosize';
// App Imports
import { AppRoutingModule } from './app-routing.module';

// App Components
import { AppComponent } from './app.component';
import { LoginComponent } from './login/login.component';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { ChangePasswordComponent } from './change-password/change-password.component';

// App Services
import { AuthService } from './_services/auth.service';
import { AuthGuardService } from './_guards/auth-guard.service';
import { SharedFunctionsService } from './_services/shared-functions.service';
import { DateFormatService } from './_services/date-format.service';
import { GlobalErrorHandlerService } from './_error-handler/global-error-handler.service';
import { DocumentTypeService } from './_services/document-type.service';
import { SpinnerService } from './_core/spinner/spinner.service';
import { SpinnerComponent } from './_core/spinner/spinner.component';
// ng-circle-progress
import { NgCircleProgressModule } from 'ng-circle-progress';
import { Ng2TelInputModule } from 'ng2-tel-input';
import { FeedbackModule } from 'ng-feedback';

import { CoreModule } from './_core/core.module';
import { RegisterComponent } from './register/register.component';
import { UserDataService } from './_services/user-data.service';
import { QuestionAnswerService } from './_services/question-answer.service';
import { TestResultsService } from './_services/test-results.service';
import { LayoutModule } from '@angular/cdk/layout';
import { BasicOfInventionDetailsService } from './_services/basic-of-invention-details.service';
import { PatentDraftService } from './_services/patent-draft.service';
import { LearningBasicsComponent } from './learning/learning-basics/learning-basics.component';
import { LearningSearchComponent } from './learning/learning-search/learning-search.component';
import { LearningDraftComponent } from './learning/learning-draft/learning-draft.component';
import { LearningDrawingComponent } from './learning/learning-drawing/learning-drawing.component';
import { LearningFilingComponent } from './learning/learning-filing/learning-filing.component';
import { TestBasicsComponent } from './test/test-basics/test-basics.component';
import { TestSearchComponent } from './test/test-search/test-search.component';
import { TestDraftComponent } from './test/test-draft/test-draft.component';
import { NavbarComponent } from './navbar/navbar.component';
import { MaterialModule } from './shared/material.module';
import { DashboardUserComponent } from './dashboard-user/dashboard-user.component';
import { SnotifyModule,ToastDefaults, SnotifyService } from 'ng-snotify';
import { OtpService } from './_services/otp.service';
import { CustomNotifyServiceService } from './_services/custom-notify-service.service';
import { DashboardAdminComponent } from './dashboard-admin/dashboard-admin.component';
import { PasswordUpdateComponent } from './password-update/password-update.component';
import { PatentDraftSaveUpdateComponent } from './patent-draft-save-update/patent-draft-save-update.component';
import { PatentSearchComponent } from './patent-search/patent-search.component';
import { QuestionAnswerListComponent } from './question-answer-list/question-answer-list.component';
import { QuestionAnswerSaveUpdateComponent } from './question-answer-save-update/question-answer-save-update.component';
import { AddressComponent } from './address/address.component';
import { AddressService } from './_services/address.service';
import { PatentSearchService } from './_services/patent-search.service';
import { BasicInventionDetailsSaveUpdateComponent } from './basic-invention-details-save-update/basic-invention-details-save-update.component';
import { LearningStatusComponent } from './learning-status/learning-status.component';
import { SuccessDialogComponent } from './success-dialog/success-dialog.component';
import { NdaComponent } from './nda/nda.component';
import { SesssionActivityService } from './_services/sesssion-activity.service';
import { DoingStatusComponent } from './doing-status/doing-status.component';
import { PreviewComponent } from './preview/preview.component';
import { PatentDrawingSaveUpdateComponent } from './patent-drawing-save-update/patent-drawing-save-update.component';
import { ProjectService } from './_services/project.service';
import { NavbarService } from './_services/navbar.service';
import { DialogComponent } from './dialog/dialog.component';
import { PatentDrawingService } from './_services/patent-drawing.service';
import { ProjectListComponent } from './project-list/project-list.component';
import { SuccessDialogService } from './_services/success-dialog.service';
import { NgxCaptchaModule } from 'ngx-captcha';
import { MaxWordCountValidator } from './shared/maxWordCountValidator.directive';
import { InventionResolver } from './_services/invention-resolver-service';
import { SuccessDialogQuizService } from './_services/success-dialog-quiz.service';
import { SuccessDialogQuizComponent } from './success-dialog-quiz/success-dialog-quiz.component';
import { AgmCoreModule } from '@agm/core';
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component';
import { PatentFilingProcessComponent } from './patent-filing-process/patent-filing-process.component';
import { DrawingDialogService } from './_services/drawing-dialog.service';
import { DrawingDialogComponent } from './drawing-dialog/drawing-dialog.component';

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    ForgotPasswordComponent,
    ChangePasswordComponent,
    SpinnerComponent,
    RegisterComponent,
    TestBasicsComponent,
    TestDraftComponent,
    TestSearchComponent,
    LearningBasicsComponent,
    LearningDrawingComponent,
    LearningSearchComponent,
    LearningDraftComponent,
    LearningFilingComponent,
    NavbarComponent,
    DashboardUserComponent,
    DashboardAdminComponent,
    PasswordUpdateComponent,
    PatentDraftSaveUpdateComponent,
    PatentDrawingSaveUpdateComponent,
    PatentSearchComponent,
    QuestionAnswerListComponent,
    QuestionAnswerSaveUpdateComponent,
    AddressComponent,
    BasicInventionDetailsSaveUpdateComponent,
    LearningStatusComponent,
    SuccessDialogComponent,
    NdaComponent,
    DoingStatusComponent,
    PreviewComponent,
    DialogComponent,
    ProjectListComponent,
    MaxWordCountValidator,
    SuccessDialogQuizComponent,
    ConfirmDialogComponent,
    PatentFilingProcessComponent,
    DrawingDialogComponent
  ],
  imports: [
    BrowserModule,
    FormsModule,
    BrowserAnimationsModule,
    MaterialModule,
    HttpClientModule,
    AppRoutingModule,
    AppRoutingModule,
    CommonModule,
    ReactiveFormsModule,
    CoreModule,
    LayoutModule,
    SnotifyModule,
    TextareaAutosizeModule,
    ChartsModule,
    NgxCaptchaModule,
    NgCircleProgressModule.forRoot({
      subtitleFontSize: '14',
      subtitleColor: '#6d6d6d'
    }),
    Ng2TelInputModule,
    AgmCoreModule.forRoot({
	    apiKey: 'AIzaSyAVr9esr_4j0QVH0Tyz21wRHBOAQZH4nfg',
	    libraries: ["places"]
    }),
    FeedbackModule
  ],
  entryComponents: [
    SuccessDialogComponent,
    DialogComponent,
    SuccessDialogQuizComponent,
    ConfirmDialogComponent
  ],  
  providers: [
    Location, { provide: LocationStrategy, useClass: PathLocationStrategy },
    AuthService,
    AuthGuardService,
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    SpinnerService,
    DocumentTypeService,
    DateFormatService,
    SharedFunctionsService,
    UserDataService,
    { provide: 'SnotifyToastConfig', useValue: ToastDefaults },
    QuestionAnswerService,
    TestResultsService,
    BasicOfInventionDetailsService,
    PatentDraftService,
    OtpService,
    SnotifyService,
    CustomNotifyServiceService,
    AddressService,
    PatentSearchService,
    SesssionActivityService,
    ProjectService,
    NavbarService,
    PatentDrawingService,
    SuccessDialogService,
    InventionResolver,
    SuccessDialogQuizService,
    DrawingDialogService
  ],
  bootstrap: [
    AppComponent
  ],
})
export class AppModule {
  location: Location;
  constructor(location: Location) { this.location = location; }
}
